import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { FacilityCard } from "@/components/FacilityCard";
import { MapView } from "@/components/MapView";
import { MapErrorBoundary } from "@/components/MapErrorBoundary";
import { FacilityDetailPanel } from "@/components/FacilityDetailPanel";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { enabledCategories } from "@/lib/categories";
import { api } from "@/lib/api";
import { track } from "@/lib/track";
import { toast } from "sonner";
import { Search, SlidersHorizontal, Map as MapIcon, List, Heart } from "lucide-react";

export default function SearchPage() {
  const { user, refresh } = useAuth();
  const { settings } = useSettings();
  const [params, setParams] = useSearchParams();
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counties, setCounties] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [hoverId, setHoverId] = useState(null);
  const [mobileMap, setMobileMap] = useState(false);
  const [listView, setListView] = useState(false);

  const [search, setSearch] = useState(params.get("q") || "");
  const [county, setCounty] = useState("all");
  const [category, setCategory] = useState("all");
  const [paymentType, setPaymentType] = useState("all");
  const [onlyAvail, setOnlyAvail] = useState(false);
  const [freshness, setFreshness] = useState("any");
  const [radius, setRadius] = useState(params.get("radius") || "3");
  const [center, setCenter] = useState(null);

  const fetchFacilities = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/facilities", {
        params: {
          search: search || undefined, county, category, payment_type: paymentType, only_available: onlyAvail,
          freshness: freshness !== "any" ? freshness : undefined,
          radius_miles: radius !== "any" && search ? Number(radius) : undefined,
        },
      });
      setFacilities(data.facilities || []);
      setCenter(data.center || null);
    } catch { toast.error("Failed to load facilities"); }
    finally { setLoading(false); }
  }, [search, county, category, paymentType, onlyAvail, freshness, radius]);

  // Only record a "search" analytics event on explicit submit (Enter / Search button),
  // not on every keystroke.
  const submitSearch = () => {
    const q = search.trim();
    if (q) track("search", { location: q });
    fetchFacilities();
  };

  useEffect(() => { track("page_view"); }, []);
  // Count a search that arrives via the URL (e.g. from the homepage hero) exactly once on mount.
  const urlSearchTracked = useRef(false);
  useEffect(() => {
    if (urlSearchTracked.current) return;
    urlSearchTracked.current = true;
    const q = (params.get("q") || "").trim();
    if (q) track("search", { location: q });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { api.get("/facilities/counties").then((r) => setCounties(r.data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(fetchFacilities, 300); return () => clearTimeout(t); }, [fetchFacilities]);

  const favSet = useMemo(() => new Set(user?.favorites || []), [user]);

  const toggleFavorite = async (f) => {
    if (!user) { toast.error("Log in to save facilities"); return; }
    try {
      await api.post(`/favorites/${f.facility_id}`);
      await refresh();
    } catch { toast.error("Could not update favorite"); }
  };

  const openDetail = (f) => { setSelected(f); setDetailOpen(true); };

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Header />

      {/* Filter bar */}
      <div className="sticky top-16 md:top-20 z-40 bg-white/85 backdrop-blur-xl border-b border-[#E8E6DF]">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-3 flex flex-wrap items-center gap-3">
          <div className="relative w-full sm:flex-1 sm:min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5C6660]" />
            <Input data-testid="search-input" value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitSearch(); } }}
              placeholder="Search city, county, or facility name..." className="pl-9 pr-24 rounded-xl border-[#E8E6DF] h-11" />
            <Button type="button" onClick={submitSearch} data-testid="search-submit"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-9 px-4 bg-[#1A365D] hover:bg-[#12283F] text-white rounded-lg text-sm">
              Search
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-3 w-full sm:contents">
          <Select value={county} onValueChange={setCounty}>
            <SelectTrigger className="w-full sm:w-[170px] rounded-xl border-[#E8E6DF] h-11" data-testid="county-filter" aria-label="Filter by county"><SelectValue placeholder="County" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All counties</SelectItem>
              {counties.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-full sm:w-[210px] rounded-xl border-[#E8E6DF] h-11" data-testid="type-filter" aria-label="Filter by facility type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All facility types</SelectItem>
              {enabledCategories(settings).map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={paymentType} onValueChange={setPaymentType}>
            <SelectTrigger className="w-full sm:w-[210px] rounded-xl border-[#E8E6DF] h-11" data-testid="payment-filter" aria-label="Filter by payment type"><SelectValue placeholder="Payment type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any payment type</SelectItem>
              {(settings?.payment_type_options || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={radius} onValueChange={setRadius}>
            <SelectTrigger className="w-full sm:w-[150px] rounded-xl border-[#E8E6DF] h-11" data-testid="radius-filter" aria-label="Filter by distance"><SelectValue placeholder="Radius" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any distance</SelectItem>
              <SelectItem value="3">Within 3 mi</SelectItem>
              <SelectItem value="5">Within 5 mi</SelectItem>
              <SelectItem value="10">Within 10 mi</SelectItem>
              <SelectItem value="25">Within 25 mi</SelectItem>
              <SelectItem value="50">Within 50 mi</SelectItem>
            </SelectContent>
          </Select>
          <Select value={freshness} onValueChange={setFreshness}>
            <SelectTrigger className="w-full sm:w-[180px] rounded-xl border-[#E8E6DF] h-11" data-testid="freshness-filter" aria-label="Filter by availability freshness"><SelectValue placeholder="Availability freshness" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any freshness</SelectItem>
              <SelectItem value="recent">Recently updated (≤10d)</SelectItem>
              <SelectItem value="fresh">Fresh only (≤4d)</SelectItem>
            </SelectContent>
          </Select>
          <label className="flex items-center justify-center sm:justify-start gap-2 px-3 h-11 rounded-xl border border-[#E8E6DF] bg-white cursor-pointer w-full sm:w-auto" data-testid="avail-toggle">
            <Switch checked={onlyAvail} onCheckedChange={setOnlyAvail} aria-label="Show only facilities with beds available" />
            <span className="text-sm text-[#2A312D] whitespace-nowrap">Beds available</span>
          </label>
          <div className="hidden md:flex items-center gap-1 bg-[#EFEEE8] rounded-xl p-1 h-11" data-testid="view-toggle">
            <button onClick={() => setListView(false)} data-testid="view-split" className={`px-3 h-9 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${!listView ? "bg-white text-[#1A365D] shadow-sm" : "text-[#5C6660]"}`}><MapIcon className="h-4 w-4" /> Map</button>
            <button onClick={() => setListView(true)} data-testid="view-list" className={`px-3 h-9 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${listView ? "bg-white text-[#1A365D] shadow-sm" : "text-[#5C6660]"}`}><List className="h-4 w-4" /> List</button>
          </div>
          </div>
        </div>
        {radius !== "any" && !search && (
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 pb-2 -mt-1">
            <span className="text-xs text-[#0077B6]">Enter a location above to search within {radius} miles.</span>
          </div>
        )}
      </div>

      {/* Split panel */}
      <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-12">
        {/* List */}
        <div className={`${listView ? "md:col-span-12" : "md:col-span-5 lg:col-span-4"} md:h-[calc(100vh-144px)] md:overflow-y-auto p-4 pb-28 md:pb-6 ${mobileMap ? "hidden md:block" : "block"}`}>
          <div className="flex items-center gap-2 mb-4 rounded-xl bg-[#E3F3ED] px-3 py-2" data-testid="trust-strip">
            <Heart className="h-4 w-4 text-[#0077B6] shrink-0" />
            <p className="text-xs text-[#1A365D] font-medium">Free for families · we never take referral commissions.</p>
          </div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-[#4A534E]" data-testid="results-count">
              {loading ? "Searching..." : <><span className="font-semibold text-[#1C211F]">{facilities.length}</span> facilities</>}
            </p>
            <SlidersHorizontal className="h-4 w-4 text-[#5C6660]" />
          </div>
          <div className={listView ? "grid sm:grid-cols-2 lg:grid-cols-3 gap-4" : "space-y-4"}>
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-64 w-full rounded-2xl" />)
              : facilities.map((f) => (
                  <FacilityCard key={f.facility_id} facility={f} active={selected?.facility_id === f.facility_id || hoverId === f.facility_id}
                    isFavorite={favSet.has(f.facility_id)} onSelect={openDetail} onToggleFavorite={toggleFavorite} onHover={setHoverId} />
                ))}
            {!loading && facilities.length === 0 && (
              <div className="text-center py-16 text-[#5C6660]">No facilities match your search.</div>
            )}
          </div>
        </div>

        {/* Map */}
        <div className={`md:col-span-7 lg:col-span-8 h-[70vh] md:h-[calc(100vh-144px)] md:sticky md:top-[144px] ${listView ? "hidden" : (mobileMap ? "block" : "hidden md:block")}`}>
          <MapErrorBoundary>
            <MapView facilities={facilities} activeId={hoverId || selected?.facility_id} center={center} radiusMiles={radius !== "any" ? Number(radius) : null} onSelect={openDetail} onHover={setHoverId} />
          </MapErrorBoundary>
        </div>
      </div>

      {/* Mobile toggle */}
      <button onClick={() => setMobileMap(!mobileMap)} data-testid="map-toggle-btn"
        className="md:hidden fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-5 py-3 rounded-full bg-[#1A365D] text-white shadow-lg backdrop-blur">
        {mobileMap ? <><List className="h-4 w-4" /> List</> : <><MapIcon className="h-4 w-4" /> Map</>}
      </button>

      <FacilityDetailPanel facility={selected} open={detailOpen} onOpenChange={setDetailOpen}
        isFavorite={selected ? favSet.has(selected.facility_id) : false} onToggleFavorite={toggleFavorite} />
    </div>
  );
}
